import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import SortableTableLabel from '../../components/Table/SortableTableLabel';
import Button from '@mui/material/Button';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import dayjs from 'dayjs';
import { styled } from '@mui/material/styles';
import { addCommas } from '../../utils/functions/commas';
import DrawerHistory from '../../components/Modal/DrawerHistory';
import SearchPayHistory from '../../components/Search/SearchPayHistory';
import Loading from '../../components/Loading';
import { useHistoryForPaymentQuery } from '../../services/paymentTransaction';
import NewDrawerHistory from '../../components/Modal/NewDrawerHistory';

import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#fff', // หรือ theme.palette.action.hover ถ้าต้องการ
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },

  // [ปรับปรุง] ย้าย :hover มาไว้ที่นี่
  '&:hover': {
    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
    position: 'relative',
    zIndex: 10,
    cursor: 'pointer', // เพิ่ม cursor เพื่อให้รู้ว่า hover ได้
  },

  transition: 'transform 0.15s ',
}));

function PaymentHistory() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const columns = [
    // { id: 'invoice_description', label: 'Invoice No.', minWidth: 90 },
    { id: 'order_id_ref', label: t('app.transactionRefNo'), minWidth: 100 },
    { id: 'invoice_no', label: t('app.invoiceNo'), minWidth: 100 },
    { id: 'description', label: t('app.serviceName'), minWidth: 100 },
    { id: 'period_start_date', label: t('app.startDate'), minWidth: 110 },
    { id: 'period_end_date', label: t('app.endDate'), minWidth: 110 },
    {
      id: 'payment_transaction_date',
      label: t('app.paymentDate'),
      minWidth: 100,
    },
    { id: 'payment_type', label: t('app.paymentMethod'), minWidth: 100 },
    { id: 'total_pay_amount', label: t('app.payAmount'), minWidth: 100 },
  ];
  const [rowsPerPage, setRowsPerPage] = useState(5);
  // หน้าบ้านเริ่ม page จาก 0 หลังบ้านเริ่ม pageจาก 1
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [total, setTotal] = useState(0);
  const [rowsTable, setRowsTable] = useState([]);
  const { value: merchant } = useSelector((state) => state.merchant);
  const { value: custNO, federalTaxId } = useSelector((state) => state.custNO);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [dataRowDetail, setDataRowDetail] = useState();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState({
    maxPayAmount: '',
    minPayAmount: '',
    payment_method: '',
    service: '',
    startDate: searchParams.get('startDate')
      ? dayjs(searchParams.get('startDate'))
      : dayjs().startOf('year'),
    endDate: searchParams.get('endDate')
      ? dayjs(searchParams.get('endDate'))
      : dayjs(),
    tran_ref: '',
  });
  // console.log('rowsTable', rowsTable);
  const { data: historyData, isLoading } = useHistoryForPaymentQuery(
    {
      customerCode: custNO || '',
      sorgcode: merchant || '',
      size: rowsPerPage,
      page: page + 1,
      paymentStatus: 'Success',

      // ปรับปรุงการเช็คเงื่อนไขใหม่ให้ป้องกัน undefined และ null
      startDate: search?.startDate
        ? dayjs(search.startDate).format('YYYYMMDD')
        : dayjs().startOf('year').format('YYYYMMDD'),
      endDate: search?.endDate
        ? dayjs(search.endDate).format('YYYYMMDD')
        : dayjs().format('YYYYMMDD'),

      service: search?.service || '',
      tran_ref: search?.tran_ref || '',
      minPayAmount: search?.minPayAmount || '',
      maxPayAmount: search?.maxPayAmount || '',
      paymentMethod: search?.payment_method || '', // <--- แก้ไขจุดนี้
      federalTaxId: federalTaxId || '',
      sortBy,
      sortOrder,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !merchant,
    },
  );
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRequestSort = (property) => {
    setSortOrder(
      sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc',
    );
    setSortBy(property);
    setPage(0);
  };

  const handleResetForm = () => {
    setSearch({
      maxPayAmount: '',
      minPayAmount: '',
      payment_method: '',
      service: '',
      startDate: dayjs().startOf('year'),
      endDate: dayjs(),
      tran_ref: '',
    });
  };

  useEffect(() => {
    if (historyData) {
      const { pagination, data: rows } = historyData;
      setTotal(pagination.total);
      setRowsTable(rows);
    }
    return () => {};
  }, [historyData]);

  return (
    <div>
      <Loading isLoading={isLoading} />
      <div className="my-2 ml-2">
        <div className="text-lg font-light items-center">
          <div className="mb-6 text-lg font-bold">
            {' '}
            {t('app.paymentHistory')}{' '}
          </div>
        </div>
      </div>

      <SearchPayHistory
        search={search}
        setSearch={setSearch}
        handleResetForm={handleResetForm}
      />

      <div className="flex-wrap mt-4 border-collapse border border-slate-200 ">
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
     <TableContainer sx={{ overflowX: 'auto', overflowY: 'visible' }}>

            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <StyledTableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      <div className="whitespace-nowrap">
                        <SortableTableLabel
                          column={column}
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleRequestSort}
                          color="white"
                        />
                      </div>
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              {_.size(rowsTable) ? (
                <TableBody>
                  {rowsTable.length > 0 &&
                    _.map(rowsTable, (item) => (
                      <StyledTableRow
                        onClick={() => {
                          setOpenDrawer(true);
                          // console.log('data', index);
                          setDataRowDetail(item);
                        }}
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={item._id}
                      >
                        {columns.map((column) => {
                          const value = item[column.id];

                          if (column.id === 'order_id_ref') {
                            return (
                              <TableCell
                                sx={{
                                  fontFamily: 'Monospace',
                                  color: 'primary.light',
                                  fontWeight: 'bold',
                                }}
                                key={column.id}
                                align={column.align}
                              >
                                {/* {subStrForInvoice(value)} */}
                                {value}
                              </TableCell>
                            );
                          }
                          if (column.id === 'period_start_date') {
                            return (
                              <TableCell key={column.id}>
                                {dayjs(value).format('YYYY-MM-DD')}
                              </TableCell>
                            );
                          }

                          if (column.id === 'period_end_date') {
                            return (
                              <TableCell key={column.id}>
                                {dayjs(value).format('YYYY-MM-DD')}
                              </TableCell>
                            );
                          }

                          if (column.id === 'payment_transaction_date') {
                            return (
                              <TableCell key={column.id}>
                                {value !== ''
                                  ? dayjs(value).format('YYYY-MM-DD')
                                  : '-'}
                              </TableCell>
                            );
                          }

                          if (column.id === 'total_pay_amount') {
                            return (
                              <TableCell key={column.id}>
                                {addCommas(value)}
                              </TableCell>
                            );
                          }
                          if (column.id === 'payment_type') {
                            return (
                              <TableCell key={column.id}>{value}</TableCell>
                            );
                          }

                          return (
                            <TableCell key={column.id} align={column.align}>
                              {value}
                            </TableCell>
                          );
                        })}
                      </StyledTableRow>
                    ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <div>ไม่มีข้อมูล</div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} of ${addCommas(count, 0)}`
            }
          />
        </Paper>
      </div>
      {/* <DrawerHistory
        openDrawer={openDrawer}
        setOpenDrawer={setOpenDrawer}
        dataRowDetail={dataRowDetail}
      /> */}
      <NewDrawerHistory
        openDrawer={openDrawer}
        setOpenDrawer={setOpenDrawer}
        dataRowDetail={dataRowDetail}
      />
    </div>
  );
}

export default PaymentHistory;
